import React, { useEffect, useState } from 'react';
import DocMarkdown from './DocMarkdown';
import { DocIcon } from './DocIcons';
import { docsApi, errorOf, fullDate } from '../../lib/docs';

/**
 * An article's history: every save, who made it and why; what changed
 * between any two; and putting an old one back (which is itself a new
 * version - nothing in the history is ever rewritten).
 */
export default function DocHistory({ id, onChanged }) {
  const [versions, setVersions] = useState(null);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [diff, setDiff] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await docsApi.get(`/docs/articles/${id}/versions`);
      setVersions(r.versions);
      if (r.versions.length > 1) { setTo(r.versions[0].number); setFrom(r.versions[1].number); }
      else if (r.versions.length) { setTo(r.versions[0].number); setFrom(r.versions[0].number); }
    } catch (e) { setErr(errorOf(e, 'Could not load the history')); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!from || !to) return undefined;
    let live = true;
    setDiff(null);
    docsApi.get(`/docs/articles/${id}/diff`, { from: Math.min(from, to), to: Math.max(from, to) })
      .then((r) => { if (live) setDiff(r); }).catch((e) => { if (live) setErr(errorOf(e)); });
    return () => { live = false; };
  }, [id, from, to]);

  const show = async (n) => {
    try { setViewing((await docsApi.get(`/docs/articles/${id}/versions/${n}`)).version); } catch (e) { setErr(errorOf(e)); }
  };
  const restore = async (n) => {
    setBusy(true); setNote(null);
    try {
      const r = await docsApi.post(`/docs/articles/${id}/restore`, { version: n });
      setNote(`Version ${n} is back, saved as version ${r.version}. Publish it when you are ready.`);
      setViewing(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) { setErr(errorOf(e)); }
    setBusy(false);
  };
  const publish = async (n) => {
    setBusy(true); setNote(null);
    try {
      await docsApi.post(`/docs/articles/${id}/publish`, { version: n });
      setNote(`Readers now see version ${n}.`);
      await load();
      if (onChanged) onChanged();
    } catch (e) { setErr(errorOf(e)); }
    setBusy(false);
  };

  if (err && !versions) return <p className="dp-error" role="alert">{err}</p>;
  if (!versions) return <p className="dp-hint" aria-busy="true">Loading the history…</p>;

  return (
    <div className="dh">
      {note && <p className="dp-saved" role="status"><DocIcon name="check" />{note}</p>}
      {err && <p className="dp-error" role="alert">{err}</p>}
      <div className="dh-grid">
        <ol className="dh-list" aria-label="Versions">
          {versions.map((v) => (
            <li key={v.number} className={`dh-v${v.number === to || v.number === from ? ' is-picked' : ''}`}>
              <div className="dh-v-head">
                <span className="dh-n">v{v.number}</span>
                {v.is_published && <span className="dh-badge dh-badge-pub">Published</span>}
                {v.is_latest && !v.is_published && <span className="dh-badge">Latest draft</span>}
              </div>
              <div className="dh-v-who">{v.author_name || 'Somebody'} · <time dateTime={v.created_at}>{fullDate(v.created_at)}</time></div>
              {(v.summary || v.restored_from) && <div className="dh-v-why">{v.summary || `Restored version ${v.restored_from}`}</div>}
              <div className="dh-v-actions">
                <label className="dh-pick"><input type="radio" name="dh-from" checked={from === v.number} onChange={() => setFrom(v.number)} />From</label>
                <label className="dh-pick"><input type="radio" name="dh-to" checked={to === v.number} onChange={() => setTo(v.number)} />To</label>
                <button type="button" className="btn btn-plain btn-small" onClick={() => show(v.number)}>View</button>
                {!v.is_latest && <button type="button" className="btn btn-plain btn-small" disabled={busy} onClick={() => restore(v.number)}>Restore</button>}
                {!v.is_published && <button type="button" className="btn btn-plain btn-small" disabled={busy} onClick={() => publish(v.number)}>Publish</button>}
              </div>
            </li>
          ))}
        </ol>
        <div className="dh-diff" aria-live="polite">
          {viewing ? (
            <div className="dh-view">
              <div className="dh-view-head">
                <strong>Version {viewing.number}</strong>
                <button type="button" className="btn btn-plain btn-small" onClick={() => setViewing(null)}>Back to the comparison</button>
              </div>
              <h2 className="da-title">{viewing.title}</h2>
              <DocMarkdown text={viewing.body} />
            </div>
          ) : !diff ? <p className="dp-hint" aria-busy="true">Comparing…</p> : (
            <>
              <div className="dh-diff-head">
                <strong>Version {diff.from.number} → {diff.to.number}</strong>
                <span className="dh-count"><span className="dh-add">+{diff.added}</span> <span className="dh-del">−{diff.removed}</span></span>
              </div>
              {diff.meta.map((m) => <p key={m.field} className="dh-meta">{m.field}: <del>{m.from}</del> → <ins>{m.to}</ins></p>)}
              {diff.from.number === diff.to.number ? <p className="dp-hint">Pick two different versions to compare.</p> : (
                <div className="dh-lines" role="table" aria-label="Changes">
                  {diff.lines.map((l, i) => (l.op === 'skip'
                    ? <div key={i} className="dh-line dh-skip" role="row"><span role="cell">… {l.count} unchanged line{l.count === 1 ? '' : 's'} …</span></div>
                    : (
                      <div key={i} className={`dh-line dh-${l.op}`} role="row">
                        <span className="dh-ln" role="cell">{l.a || ''}</span>
                        <span className="dh-ln" role="cell">{l.b || ''}</span>
                        <span className="dh-sign" role="cell" aria-label={l.op === 'add' ? 'added' : l.op === 'del' ? 'removed' : 'unchanged'}>{l.op === 'add' ? '+' : l.op === 'del' ? '−' : ' '}</span>
                        <code className="dh-text" role="cell">{l.text || ' '}</code>
                      </div>
                    )))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
